/*
 * Handle a form containing a search bar with multiple filters
 * - Validation of range field
 * - Use dropdown to display filter
 * - Display selected values on button dropdown
 * - Does not handle form submission
 */
import 'qtip2';

import $ from 'jquery';
import Numeral from 'numeral';

import popinTemplate from './templates/popin.html';
import tagTemplate from './templates/tag.html';
import { SEARCH_FILTERS_MAX_SELETED_PLACES } from './constants.es6';
import { isMobile, isMobileOrTablet } from './device';
import {
    addClass,
    getParent,
    getParents,
    hasClass,
    hasParent,
    removeClass,
} from './domHelpers.es6';
import {
    removeErrors,
    setFieldError,
    validateNumberField,
    validateRangeAfterInitialValidation,
} from './form.es6';
import MAEvent from './ma-event';
import { initializePopin } from './popin.es6';
import { initializePlacesTypeahead } from './search';
import { displayErrorToaster } from './toaster.es6';

/**
 * Update the label of the criteria button
 * Use data-* to decide which behavior to adopt
 * @param {HTMLFormElement} filterField
 */
function updateFilterLabel(filterField) {
    const {
        name,
        dataset: { filterTarget, filterType, filterName, filterUnits },
    } = filterField;
    const target = document.querySelector(filterTarget);
    const values = [];
    let buttonLabel;

    if (!target) {
        return;
    }

    switch (filterType) {
        case 'rooms': {
            document.querySelectorAll(`[name=${name}]`).forEach(field => {
                if (field.checked) {
                    values.push(field.dataset.filterLabel);
                }
            });
            buttonLabel = values.length ? `${values.join(', ')} pièce(s)` : null;
            break;
        }
        case 'places': {
            if (MA.Context.searchFilterPlaces) {
                buttonLabel = MA.Context.searchFilterPlaces
                    .map(place => place.name_short)
                    .join(', ');
            }
            break;
        }
        case 'range': {
            const minField = document.querySelector(`[name=min_${filterName}]`);
            const min = (minField && minField.value) || null;
            const maxField = document.querySelector(`[name=max_${filterName}]`);
            const max = (maxField && maxField.value) || null;
            if (min && max) {
                buttonLabel = `${Numeral(min).format('0,0')} - ${Numeral(max).format(
                    '0,0',
                )} ${filterUnits || ''}`;
            } else if (min) {
                buttonLabel = `+ de ${Numeral(min).format('0,0')} ${filterUnits || ''}`;
            } else if (max) {
                buttonLabel = `- de ${Numeral(max).format('0,0')} ${filterUnits || ''}`;
            }
            break;
        }
        case 'list': {
            document.querySelectorAll(`[name=${name}]`).forEach(field => {
                if (field.checked) {
                    values.push(field.dataset.filterLabel);
                }
            });
            buttonLabel = values.join(', ');
            break;
        }
        default:
            break;
    }

    const changedClassName = 'search-filter__button--changed';

    if (buttonLabel) {
        if (target.innerHTML !== buttonLabel) {
            target.innerHTML = buttonLabel;
            target.title = buttonLabel;

            addClass(target, changedClassName);
        }
    } else {
        target.innerHTML = 'Choisir';
        target.title = '';
        removeClass(target, changedClassName);
    }
}

/**
 * Validate the form containing search filters
 * @param {HTMLFormElement} form Form to validate
 */
export function validateForm(form) {
    let errors = 0;
    const minPrice = 1;
    const maxPrice = 1000000000;
    const minArea = 1;
    const maxArea = 60000;

    if (!validateNumberField(form, 'min_price', minPrice, maxPrice, 'Prix', '€')) {
        errors += 1;
    }

    if (!validateNumberField(form, 'max_price', minPrice, maxPrice, 'Prix', '€')) {
        errors += 1;
    }

    if (!validateNumberField(form, 'min_area', minArea, maxArea, 'Surface', 'm²')) {
        errors += 1;
    }

    if (!validateNumberField(form, 'max_area', minArea, maxArea, 'Surface', 'm²')) {
        errors += 1;
    }

    if (
        !validateRangeAfterInitialValidation(
            form,
            'price',
            'Le prix maximum doit être supérieur au prix minimum.',
        )
    ) {
        errors += 1;
    }

    if (
        !validateRangeAfterInitialValidation(
            form,
            'area',
            'La surface maximale doit être supérieure à la surface minimale.',
        )
    ) {
        errors += 1;
    }

    return errors === 0;
}

/**
 * Bind event to trigger validation
 * @param {HTMLFormElement} form Form to initialize validation
 * @param {Function} validate Function to validate form inputs
 */
export function initializeValidation(form, validate = validateForm, onFormSubmit = () => {}) {
    const textFields = form.querySelectorAll('[type=number]');
    textFields.forEach(textField => {
        $(textField).on('textchange', () => {
            validate(form);
        });
    });

    form.addEventListener('submit', event => {
        event.preventDefault();
        event.stopPropagation();

        if (!validate(form)) {
            displayErrorToaster('Votre recherche comporte des erreurs.');
            return;
        }

        $.magnificPopup.close();

        onFormSubmit(form);
    });
}

/**
 * Generate a list of tags representing selected places
 * @requires MA.Context.searchFilterPlaces
 */
export function updatePlaceTags() {
    const $selectedPlaces = $('[data-selected-places]');
    $selectedPlaces.html('');
    MA.Context.searchFilterPlaces.forEach(place => {
        const tag = tagTemplate({
            colorClass: 'blue',
            attrs: `data-place-id="${place.id}"`,
            label: place.name_short,
        });
        $selectedPlaces.append(tag);
    });
    $('[name=place_ids]').val(MA.Context.searchFilterPlaces.map(place => place.id).join(','));
    updateFilterLabel(document.querySelector('[data-places-search]'));
    $.event.trigger({
        type: MAEvent.searchFiltersPlaceTagsUpdated,
        selectedPlaces: MA.Context.searchFilterPlaces,
    });
}

/**
 * Remove a selected place from context and update the view
 * @param {String|Number} placeId
 */
export function removeSelectedPlace(placeId) {
    MA.Context.searchFilterPlaces = MA.Context.searchFilterPlaces.filter(
        place => String(placeId) !== String(place.id),
    );
    updatePlaceTags();
}

/**
 * Add a selected place to context and update the view
 * @param {Object} newPlace Place GeoAPI representation
 */
export function addSelectedPlace(newPlace) {
    const exists = MA.Context.searchFilterPlaces.find(
        place => String(newPlace.id) === String(place.id),
    );
    if (!exists) {
        MA.Context.searchFilterPlaces.push(newPlace);
    }
    updatePlaceTags();
}

/**
 * Add behavior for place field
 * @param {jQuery} $placeField The place field handled by typeahead
 * @param {jQuery} $selectedPlaces The placeholder of selected places tags
 */
export function initializePlaceField($placeField, $selectedPlaces, $searchForm) {
    initializePlacesTypeahead($placeField);

    // Save selected context on place change
    $placeField.on(MAEvent.placeSelected, event => {
        const { place } = event;

        if (place) {
            if ($selectedPlaces) {
                $placeField.typeahead('val', '');
                addSelectedPlace(place);
            } else {
                $placeField.typeahead('val', place.name);
                $('[name=place_ids]').val(place.id);

                if (isMobileOrTablet()) {
                    $searchForm.submit();
                }
            }
        }
    });

    if ($selectedPlaces) {
        // Initialize place tags
        if (
            Array.isArray(MA.Context.searchFilterPlaces) &&
            MA.Context.searchFilterPlaces.length > 0
        ) {
            updatePlaceTags();
        }

        $selectedPlaces.on('click', '[data-close]', event => {
            event.preventDefault();
            event.stopPropagation();
            removeSelectedPlace($(event.target).data('place-id'), $placeField);
        });

        $(document).on(MAEvent.searchFiltersPlaceTagsUpdated, event => {
            const $field = $('[name="_places"]');
            if (event.selectedPlaces.length < SEARCH_FILTERS_MAX_SELETED_PLACES) {
                $placeField.prop('disabled', false);
                setFieldError($field, []);
            } else {
                $placeField.prop('disabled', true);
                setFieldError($field, [
                    `Vous ne pouvez pas sélectionner plus de ${SEARCH_FILTERS_MAX_SELETED_PLACES} lieux`,
                ]);
            }
        });
    }
}

/**
 * initialize behaviour of the dropdown filter in a form
 * @param {HTMLFormElement} form
 */
export function initializeDropdowns(form) {
    const toolbarButtons = document.querySelectorAll('[data-search-filter]');
    let dropdownOpened;

    function removeDropdown() {
        const errors = $(this).find('[data-field-error]');

        if (!dropdownOpened) {
            return;
        }
        const toolbarButton = this.previousElementSibling;

        // closing the dropdown
        removeClass(this, this.dataset.openedClass);
        removeClass(toolbarButton, toolbarButton.dataset.openedClass);

        // form error handling in the dropdown
        if (errors && errors.length && errors.length > 0) {
            // wipe input fields value when there is an error
            const inputsInDropDown = this.querySelectorAll('input');

            if (inputsInDropDown && inputsInDropDown.length > 0) {
                inputsInDropDown.forEach(input => {
                    // reset input value
                    /* eslint-disable no-param-reassign */
                    input.value = '';

                    // get input direct parent element to remove red border style
                    const inputParentElement = getParent(input);

                    if (inputParentElement && hasClass(inputParentElement, 'field--invalid')) {
                        removeClass(inputParentElement, 'field--invalid');
                    }
                });
            }

            // clear up any message error inside of dropdown
            removeErrors($(form));
            // removing the class we add to indicate input has been filled
            removeClass(toolbarButton, 'search-filter__button--changed');
            toolbarButton.innerHTML = 'Choisir'; // reset to placeholder default text
        }

        dropdownOpened = null;
    }

    function openDropdown() {
        const toolbarButton = this.previousElementSibling;
        addClass(this, this.dataset.openedClass);
        addClass(toolbarButton, toolbarButton.dataset.openedClass);
        dropdownOpened = this;
    }

    toolbarButtons.forEach(toolbarButton => {
        const dropdown = toolbarButton.nextElementSibling;
        if (!dropdown) {
            return;
        }

        function outsideHandler(event) {
            if (event.target.matches('[data-search-filter]')) {
                return;
            }
            if (event.target.matches('[data-search-filter-dropdown]')) {
                return;
            }
            const parents = getParents(event.target);
            // If I am not a child of '[data-search-filter-dropdown]' and
            // I am a child of HTML root
            // Avoid bubbling event on remove Element (like TypeAhead)
            if (
                !hasParent(event.target, '[data-search-filter-dropdown]') &&
                parents.length > 0 &&
                parents.splice(-1)[0].nodeName === 'HTML'
            ) {
                removeDropdown.apply(dropdownOpened);
                document.body.removeEventListener('click', outsideHandler);
            }
        }

        toolbarButton.addEventListener('click', event => {
            event.preventDefault();
            if (!dropdown) {
                return;
            }
            if (dropdownOpened && dropdownOpened !== dropdown) {
                removeDropdown.apply(dropdownOpened);
            }
            if (hasClass(dropdown, dropdown.dataset.openedClass)) {
                removeDropdown.apply(dropdown);
            } else {
                openDropdown.apply(dropdown);
            }

            // Add event to remove dropdown on elsewhere click
            document.body.addEventListener('click', outsideHandler);
        });
    });
}

/**
 * Update button dropdown label
 */
export function updateFilterFields() {
    const filterFields = document.querySelectorAll('[data-filter-type]');
    filterFields.forEach(filterField => {
        filterField.addEventListener('change', event => updateFilterLabel(event.target));
        updateFilterLabel(filterField);
    });
}

/**
 * Handle search filter in toolbar
 * @param {HTMLFormElement} form
 * @param {jQuery} $placeField The place field handled by typeahead
 * @param {jQuery} $selectedPlaces The placeholder of selected places tags
 * @param {Function} initValidation Initializeform validation
 */
export function initializeFilters(
    form,
    $placeField,
    $selectedPlaces,
    initValidation = initializeValidation,
) {
    // Transform toolbar to popin and inject it inside
    if (isMobile()) {
        const toolbar = document.querySelector('[data-search-filters-form]');
        const wrapper = document.createElement('div');

        removeClass(toolbar, 'hide-sm');
        wrapper.innerHTML = popinTemplate({
            classes: 'mfp-hide',
            bodyClasses: 'popin__body--with-sticky-cta',
            id: 'popin-filters',
            title: 'Rechercher',
        });
        toolbar.parentNode.insertBefore(wrapper, toolbar);
        wrapper.querySelector('[data-popin-body]').appendChild(toolbar);
    }

    const openFiltersPopinButton = document.querySelector('[data-open-filters-popin]');

    if (openFiltersPopinButton) {
        openFiltersPopinButton.addEventListener('click', e => {
            e.preventDefault();
            initializePopin('#popin-filters', undefined, 'inline');
        });
    }

    initializeDropdowns(form);
    updateFilterFields();

    initializePlaceField($placeField, $selectedPlaces);
    initValidation(form);
}
